// src/pages/AbonnementsDetails.js - VERSION FINALE CORRIGÉE
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import PaiementNitaAmanaModal from "../components/PaiementNitaAmanaModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Compte à rebours jusqu'à l'expiration (24h) d'un code d'achat NITA/Amana.
const CompteARebours = ({ createdAt }) => {
  const [texte, setTexte] = useState('');

  useEffect(() => {
    const calculer = () => {
      const limite = new Date(createdAt.replace(' ', 'T')).getTime() + 24 * 60 * 60 * 1000;
      const restantMs = limite - Date.now();
      if (restantMs <= 0) {
        setTexte('Référence expirée');
        return;
      }
      const heures = Math.floor(restantMs / (1000 * 60 * 60));
      const minutes = Math.floor((restantMs % (1000 * 60 * 60)) / (1000 * 60));
      setTexte(`Temps restant pour payer : ${heures}h ${minutes}min`);
    };
    calculer();
    const interval = setInterval(calculer, 60000);
    return () => clearInterval(interval);
  }, [createdAt]);

  return <span>{texte}</span>;
};

const AbonnementsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [abonnement, setAbonnement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPaiementModal, setShowPaiementModal] = useState(false);

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  useEffect(() => {
    // Récupérer le profil utilisateur
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentProfile(String(user.profile));
        setCurrentUser(user);
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }

    if (id) {
      fetchAbonnementDetails();
    }
  }, [id]);

  // Récupérer les détails de l'abonnement
  const fetchAbonnementDetails = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      const url = `${API_BASE_URL}/abonnements/get.php?id=${id}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success && response.data.abonnement) {
        setAbonnement(response.data.abonnement);
      } else {
        setError(response.data?.message || "Abonnement non trouvé.");
      }
    } catch (err) {
      console.error("Erreur chargement abonnement:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Accès interdit. Vous n'avez pas la permission de voir cet abonnement.");
      } else if (err.response?.status === 404) {
        setError("Abonnement non trouvé.");
      } else {
        setError("Erreur lors du chargement des détails.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Formater le prix en FCFA
  const formatPrix = (montant) => {
    const montantArrondi = Math.round(parseFloat(montant) || 0);
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantArrondi);
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifié";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formater la date simple
  const formatDateSimple = (dateString) => {
    if (!dateString) return "Non spécifié";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculer les jours restants
  const calculateJoursRestants = (dateFin) => {
    if (!dateFin) return null;
    const fin = new Date(dateFin);
    const maintenant = new Date();
    const diffTime = fin - maintenant;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Obtenir le statut de l'abonnement
  const getStatutAbonnement = () => {
    if (!abonnement) return null;
    
    const joursRestants = calculateJoursRestants(abonnement.date_fin);
    const estActif = abonnement.statut_abonnement === 1;
    
    if (!estActif) {
      // Paiement en ligne (myNITA/Amana) jamais finalisé — à distinguer d'un
      // abonnement simplement désactivé, pour proposer de reprendre le
      // paiement plutôt qu'un "renouvellement" qui n'a pas de sens ici.
      if (abonnement.mode_paiement === 'en_ligne' && abonnement.statut_paiement === 'pending') {
        const expire = abonnement.paiement_en_attente?.expire;
        return {
          label: expire ? 'Paiement expiré' : 'Paiement en attente',
          color: COLORS.warning,
          bgColor: '#FFF3E0',
          icon: expire ? 'bi-clock-history' : 'bi-hourglass-split',
          description: expire
            ? "Le code de paiement a expiré (24h)"
            : "Le paiement de cet abonnement n'a pas été finalisé"
        };
      }
      return {
        label: 'Inactif',
        color: COLORS.darkText,
        bgColor: '#F5F5F5',
        icon: 'bi-pause-circle',
        description: 'Cet abonnement est désactivé'
      };
    }
    
    if (joursRestants < 0) {
      return {
        label: 'Expiré',
        color: COLORS.danger,
        bgColor: '#FFEBEE',
        icon: 'bi-exclamation-circle',
        description: 'Cet abonnement a expiré'
      };
    }
    
    if (joursRestants <= 7) {
      return {
        label: `Expire dans ${joursRestants} jours`,
        color: COLORS.warning,
        bgColor: '#FFF3E0',
        icon: 'bi-clock',
        description: 'Renouvellement imminent'
      };
    }
    
    return {
      label: 'Actif',
      color: COLORS.success,
      bgColor: '#E8F5E9',
      icon: 'bi-check-circle',
      description: 'Cet abonnement est actif'
    };
  };

  // Obtenir le type d'abonnement
  const getTypeAbonnement = (type) => {
    switch(type) {
      case 1: return { label: 'Mensuel', icon: 'bi-calendar-month', color: COLORS.info };
      case 2: return { label: 'Trimestriel', icon: 'bi-calendar3', color: COLORS.secondary };
      case 3: return { label: 'Semestriel', icon: 'bi-calendar2-range', color: COLORS.accent };
      case 4: return { label: 'Annuel', icon: 'bi-calendar-year', color: COLORS.primary };
      default: return { label: 'Personnalisé', icon: 'bi-calendar', color: COLORS.darkText };
    }
  };

  // Obtenir le type d'offre
  const getTypeOffre = (type) => {
    switch(type) {
      case 'annuel': return { label: 'Annuel', icon: 'bi-star-fill', color: '#0B3C5D' };
      case 'mensuel': return { label: 'Mensuel', icon: 'bi-star', color: '#1E88C8' };
      case 'trimestriel': return { label: 'Trimestriel', icon: 'bi-three-dots', color: '#4FC3F7' };
      case 'semestriel': return { label: 'Semestriel', icon: 'bi-circle-half', color: '#2196f3' };
      default: return { label: 'Standard', icon: 'bi-tag', color: '#6c757d' };
    }
  };

  // Obtenir l'email sécurisé
  const getSafeEmail = (email) => {
    if (!email) return "Non spécifié";
    const parts = email.split('@');
    if (parts.length === 2) {
      const username = parts[0];
      const domain = parts[1];
      if (username.length <= 4) {
        return `***@${domain}`;
      }
      return `${username.substring(0, 3)}***@${domain}`;
    }
    return "***@***";
  };

  // Gérer la suppression
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      // TODO: Implémenter l'appel API pour la suppression
      setTimeout(() => {
        setDeleteLoading(false);
        setShowDeleteModal(false);
        alert("Fonctionnalité de suppression à implémenter");
        navigate("/abonnements");
      }, 1500);
    } catch (err) {
      console.error("Erreur suppression:", err);
      setDeleteLoading(false);
    }
  };

  // Télécharger la facture
  const handleDownloadInvoice = () => {
    // TODO: Implémenter le téléchargement de facture
    alert("Fonctionnalité de téléchargement de facture à implémenter");
  };

  // Vérifier si l'utilisateur peut modifier
  const canModify = () => {
    return currentProfile === "1"; // Admin seulement
  };

  // Vérifier si l'utilisateur est le propriétaire
  const isOwner = () => {
    if (!abonnement || !currentUser) return false;
    return currentProfile === "3" && currentUser.societe_id == abonnement.id_societe;
  };

  // Calculer le prix mensuel
  const getPrixMensuel = () => {
    if (!abonnement) return 0;
    const type = abonnement.type_abonnment;
    const mois = type === 1 ? 1 : type === 2 ? 3 : type === 3 ? 6 : type === 4 ? 12 : 1;
    return abonnement.montant / mois;
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'abonnement</h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary, width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3" style={{ color: COLORS.darkText }}>
              Chargement des détails de l'abonnement...
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !abonnement) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'abonnement</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.secondary }}>
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/abonnements" style={{ color: COLORS.secondary }}>
                  Abonnements
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>Détails</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ffebee',
                  color: COLORS.danger
                }}>
                  <i className="bi bi-exclamation-triangle fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Erreur</h5>
              <p className="text-muted mb-4" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                {error || "Abonnement non trouvé"}
              </p>
              
              <div className="d-flex justify-content-center gap-3">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/abonnements")}
                  style={{
                    color: COLORS.primary,
                    border: `2px solid ${COLORS.primary}`,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux abonnements
                </button>
                
                <button
                  className="btn fw-semibold"
                  onClick={fetchAbonnementDetails}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const statut = getStatutAbonnement();
  const typeAbonnement = getTypeAbonnement(abonnement.type_abonnment);
  const typeOffre = getTypeOffre(abonnement.offre?.type_offre);
  const joursRestants = calculateJoursRestants(abonnement.date_fin);
  const prixMensuel = getPrixMensuel();

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>
          <i className="bi bi-calendar-check me-2"></i>
          Détails de l'abonnement
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.secondary }}>
                Dashboard
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/abonnements" style={{ color: COLORS.secondary }}>
                Abonnements
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>
              {abonnement.libele_abonnement || "Détails"}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          {/* Colonne principale - Détails */}
          <div className="col-lg-8">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderTop: `4px solid ${COLORS.primary}`
            }}>
              <div className="card-body">
                {/* En-tête avec statut */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                  <div>
                    <h5 className="card-title m-0" style={{ color: COLORS.primary, fontWeight: '600', fontFamily: "'Inter', sans-serif" }}>
                      <i className="bi bi-file-earmark-text me-2"></i>
                      {abonnement.libele_abonnement}
                    </h5>
                  </div>
                  
                  <div className="d-flex flex-wrap gap-2">
                    <span className="badge fw-semibold d-inline-flex align-items-center" style={{ 
                      backgroundColor: statut.bgColor,
                      color: statut.color,
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontFamily: "'Inter', sans-serif",
                      border: `1px solid ${statut.color}20`
                    }}>
                      <i className={`bi ${statut.icon} me-2`}></i>
                      {statut.label}
                    </span>
                    
                    <span className="badge fw-semibold d-inline-flex align-items-center" style={{ 
                      backgroundColor: `${typeAbonnement.color}15`,
                      color: typeAbonnement.color,
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontFamily: "'Inter', sans-serif",
                      border: `1px solid ${typeAbonnement.color}30`
                    }}>
                      <i className={`bi ${typeAbonnement.icon} me-2`}></i>
                      {typeAbonnement.label}
                    </span>
                  </div>
                </div>

                {/* Informations principales */}
                <div className="row mb-4">
                  <div className="col-md-6 mb-3">
                    <div className="card h-100" style={{ 
                      border: `1px solid ${COLORS.lightBg}`,
                      borderRadius: '8px',
                      backgroundColor: '#E3F2FD'
                    }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: COLORS.primary,
                            color: COLORS.white
                          }}>
                            <i className="bi bi-building"></i>
                          </div>
                          <div>
                            <h6 style={{ color: COLORS.primary, fontWeight: '600', marginBottom: '4px' }}>Société</h6>
                            <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              {abonnement.nom_societe}
                            </div>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="card h-100" style={{ 
                      border: `1px solid ${COLORS.lightBg}`,
                      borderRadius: '8px',
                      backgroundColor: '#E8F5E9'
                    }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: COLORS.success,
                            color: COLORS.white
                          }}>
                            <i className="bi bi-cash-stack"></i>
                          </div>
                          <div>
                            <h6 style={{ color: '#2e7d32', fontWeight: '600', marginBottom: '4px' }}>Montant total</h6>
                            <div style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.3rem' }}>
                              {formatPrix(abonnement.montant)}
                            </div>
                          </div>
                        </div>
                        <div className="small" style={{ color: '#2e7d32' }}>
                          <i className="bi bi-calculator me-1"></i>
                          {formatPrix(prixMensuel)}/mois
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Détails de la période */}
                <div className="card mb-4" style={{ 
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: '8px'
                }}>
                  <div className="card-header" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    <i className="bi bi-calendar-range me-2"></i>
                    Période d'abonnement
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-4">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                              width: '36px',
                              height: '36px',
                              backgroundColor: '#E3F2FD',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-calendar-plus"></i>
                            </div>
                            <div>
                              <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>Date de début</div>
                              <div style={{ color: COLORS.darkText, fontSize: '1rem' }}>
                                {formatDate(abonnement.date_debut)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="mb-4">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                              width: '36px',
                              height: '36px',
                              backgroundColor: '#E8F5E9',
                              color: COLORS.success
                            }}>
                              <i className="bi bi-calendar-check"></i>
                            </div>
                            <div>
                              <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>Date de fin</div>
                              <div style={{ color: COLORS.darkText, fontSize: '1rem' }}>
                                {formatDate(abonnement.date_fin)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {joursRestants !== null && (
                      <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-clock me-1"></i>
                            Temps restant
                          </div>
                          <div style={{ color: joursRestants <= 7 ? COLORS.warning : COLORS.success, fontWeight: '600' }}>
                            {joursRestants > 0 ? `${joursRestants} jour${joursRestants > 1 ? 's' : ''}` : 'Expiré'}
                          </div>
                        </div>
                        
                        <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                          <div 
                            className="progress-bar" 
                            role="progressbar" 
                            style={{ 
                              width: joursRestants > 0 ? `${Math.max(5, Math.min(100, (joursRestants / 30) * 100))}%` : '100%',
                              backgroundColor: joursRestants <= 0 ? COLORS.danger : joursRestants <= 7 ? COLORS.warning : COLORS.success
                            }}
                            aria-valuenow={joursRestants} 
                            aria-valuemin="0" 
                            aria-valuemax="30"
                          ></div>
                        </div>
                        
                        {joursRestants <= 7 && joursRestants > 0 && (
                          <div className="alert alert-warning mt-3" style={{ 
                            backgroundColor: '#FFF3E0',
                            borderColor: COLORS.warning,
                            borderRadius: '8px',
                            padding: '12px'
                          }}>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-exclamation-triangle me-2" style={{ color: COLORS.warning }}></i>
                              <div>
                                <strong>Renouvellement imminent</strong>
                                <div className="small">Cet abonnement expire bientôt.</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {joursRestants <= 0 && (
                          <div className="alert alert-danger mt-3" style={{ 
                            backgroundColor: '#FFEBEE',
                            borderColor: COLORS.danger,
                            borderRadius: '8px',
                            padding: '12px'
                          }}>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-exclamation-triangle me-2" style={{ color: COLORS.danger }}></i>
                              <div>
                                <strong>Abonnement expiré</strong>
                                <div className="small">Cet abonnement a expiré et doit être renouvelé.</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Informations sur l'offre */}
                <div className="card mb-4" style={{ 
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: '8px'
                }}>
                  <div className="card-header" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    <i className="bi bi-box me-2"></i>
                    Offre souscrite
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Nom de l'offre
                          </div>
                          <div style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.1rem' }}>
                            {abonnement.offre?.libele_offre || 'Non spécifié'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Type d'offre
                          </div>
                          <span className="badge fw-semibold d-inline-flex align-items-center" style={{ 
                            backgroundColor: `${typeOffre.color}15`,
                            color: typeOffre.color,
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            border: `1px solid ${typeOffre.color}30`
                          }}>
                            <i className={`bi ${typeOffre.icon} me-2`}></i>
                            {typeOffre.label}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {abonnement.offre?.duree_mois && (
                      <div className="mt-3">
                        <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                          Durée contractuelle
                        </div>
                        <div className="d-flex align-items-center">
                          <i className="bi bi-calendar-month me-2" style={{ color: COLORS.secondary }}></i>
                          <span style={{ color: COLORS.darkText, fontSize: '1rem' }}>
                            {abonnement.offre.duree_mois} mois
                            {abonnement.offre.duree_mois >= 12 && ` (${(abonnement.offre.duree_mois / 12).toFixed(1)} an${abonnement.offre.duree_mois > 12 ? 's' : ''})`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Consommation des quotas (clients/sites/tags) de l'offre */}
                {abonnement.quotas && (
                  <div className="card mb-4" style={{
                    border: `1px solid ${COLORS.lightBg}`,
                    borderRadius: '8px'
                  }}>
                    <div className="card-header" style={{
                      backgroundColor: COLORS.lightBg,
                      borderBottom: `1px solid ${COLORS.lightBg}`,
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <i className="bi bi-pie-chart me-2"></i>
                      Consommation des quotas
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {[
                          { key: 'clients', label: 'Clients', icon: 'bi-people', color: '#2196f3' },
                          { key: 'sites', label: 'Sites', icon: 'bi-building', color: COLORS.success },
                          { key: 'tag_qr', label: 'Tags QR', icon: 'bi-qr-code', color: COLORS.warning },
                          { key: 'tag_nfc', label: 'Tags NFC', icon: 'bi-wifi', color: '#9c27b0' },
                        ].map(({ key, label, icon, color }) => {
                          const q = abonnement.quotas[key] || { utilises: 0, total: 0, pourcentage: 0 };
                          const proche = q.total > 0 && q.pourcentage >= 90;
                          return (
                            <div key={key} className="col-md-6 mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                                  <i className={`bi ${icon} me-2`} style={{ color }}></i>
                                  {label}
                                </span>
                                <span className="fw-semibold" style={{ color: proche ? COLORS.danger : COLORS.primary, fontSize: '0.95rem' }}>
                                  {q.utilises} sur {q.total}
                                </span>
                              </div>
                              <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${Math.min(100, q.pourcentage)}%`,
                                    backgroundColor: proche ? COLORS.danger : color,
                                    borderRadius: '4px'
                                  }}
                                  aria-valuenow={q.pourcentage}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-2 p-3 rounded d-flex align-items-start" style={{ backgroundColor: COLORS.lightBg }}>
                        <i className="bi bi-geo-alt-fill me-2 mt-1" style={{ color: COLORS.secondary }}></i>
                        <div style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                          <strong>Tags NFC</strong> : à récupérer en personne chez iContinuum —{' '}
                          Boulevard Saraounia, goudron Nyala, Niamey, Niger.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Informations techniques */}
                <div className="card" style={{
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: '8px'
                }}>
                  <div className="card-header" style={{
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    <i className="bi bi-gear me-2"></i>
                    Informations techniques
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Date de création
                          </div>
                          <div style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                            <i className="bi bi-clock-history me-1"></i>
                            {formatDate(abonnement.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Statut technique
                          </div>
                          <div className="d-flex align-items-center">
                            <div className={`rounded-circle me-2 ${abonnement.statut_abonnement === 1 ? 'bg-success' : 'bg-secondary'}`} 
                              style={{ width: '10px', height: '10px' }}>
                            </div>
                            <span style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                              {abonnement.statut_abonnement === 1 ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne latérale - Actions et résumé */}
          <div className="col-lg-4">
            {/* Carte d'actions */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderTop: `4px solid ${COLORS.secondary}`
            }}>
              <div className="card-header" style={{ 
                backgroundColor: COLORS.lightBg,
                borderBottom: `1px solid ${COLORS.lightBg}`,
                color: COLORS.primary,
                fontWeight: '600',
                fontFamily: "'Inter', sans-serif"
              }}>
                <i className="bi bi-lightning-charge me-2"></i>
                Actions rapides
              </div>
             <div className="card-body">
  {/* Ligne d'icônes d'actions */}
  <div className="d-flex flex-column gap-3 mb-4">
    {abonnement?.mode_paiement === 'en_ligne' && abonnement?.statut_paiement === 'pending' ? (
      abonnement?.paiement_en_attente && !abonnement.paiement_en_attente.expire && abonnement.paiement_en_attente.gateway ? (
        /* Code d'achat encore valide : on ne propose PAS de relancer la modal
           (ça générerait un second code pour le même abonnement). On affiche
           juste la référence à utiliser côté mobile et le temps restant — le
           système confirme automatiquement dès que le client paie. */
        <div className="p-3 rounded" style={{ backgroundColor: '#FFF3E0', border: `1px solid ${COLORS.warning}` }}>
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-hourglass-split me-2" style={{ color: COLORS.warning }}></i>
            <strong style={{ color: COLORS.darkText }}>Paiement en attente</strong>
          </div>
          <div className="small mb-1" style={{ color: COLORS.darkText }}>
            Référence ({abonnement.paiement_en_attente.gateway === 'nita' ? 'NITA' : 'Amana'}) :{' '}
            <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>
              {abonnement.paiement_en_attente.code_achat || abonnement.paiement_en_attente.reference}
            </span>
          </div>
          <div className="small" style={{ color: COLORS.darkText }}>
            <CompteARebours createdAt={abonnement.paiement_en_attente.created_at} />
          </div>
          <div className="small mt-2" style={{ color: '#757575' }}>
            Le client peut payer via l'application mobile avec cette référence — la confirmation est automatique.
          </div>
        </div>
      ) : (
        /* Référence expirée (24h), ou aucun moyen de paiement encore choisi
           (modal fermée sans sélection) : dans les deux cas, aucun code n'a
           été généré côté passerelle, donc aucun risque à relancer la modal. */
        <button
          type="button"
          onClick={() => setShowPaiementModal(true)}
          disabled={!abonnement?.id_paiement_pending}
          className="btn d-flex align-items-center justify-content-center fw-semibold"
          style={{
            color: COLORS.white,
            backgroundColor: COLORS.warning,
            borderColor: COLORS.warning,
            borderRadius: '8px',
            padding: '10px 20px',
            transition: 'all 0.3s ease',
            marginTop: '20px'
          }}
          title={abonnement?.paiement_en_attente?.gateway ? "Générer une nouvelle référence" : "Choisir un moyen de paiement"}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          {abonnement?.paiement_en_attente?.gateway
            ? 'Générer une nouvelle référence (expirée)'
            : 'Choisir un moyen de paiement'}
        </button>
      )
    ) : abonnement?.offre?.type_offre === 'essai' ? (
      /* Une période d'essai ne se "renouvelle" pas, et l'action "Souscrire un
         abonnement" est déjà proposée globalement (Dashboard) — pas besoin de
         la dupliquer sur la fiche de l'essai lui-même. */
      null
    ) : (
      /* Bouton Renouveler (abonnement déjà actif au moins une fois) - Redirige vers la page de renouvellement */
      <Link
        to={`/abonnements/renew/${id}`}
        className="btn d-flex align-items-center justify-content-center fw-semibold"
        style={{
          color: COLORS.white,
          backgroundColor: COLORS.success,
          borderColor: COLORS.success,
          borderRadius: '8px',
          padding: '10px 20px',
          transition: 'all 0.3s ease',
          textDecoration: 'none',
          marginTop: '20px'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#2e7d32';
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 4px 8px rgba(46, 125, 50, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = COLORS.success;
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = 'none';
        }}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title="Renouveler l'abonnement"
      >
        <i className="bi bi-arrow-repeat me-2"></i>
        Renouveler
      </Link>
    )}

    {/* Icônes Admin seulement */}
    {canModify() && (
      <div className="d-flex justify-content-center gap-3">
        {/* Icône Modifier */}
        <Link
          to={`/abonnements/edit/${id}`}
          className="btn d-flex align-items-center justify-content-center"
          style={{
            color: COLORS.white,
            backgroundColor: COLORS.secondary,
            borderColor: COLORS.secondary,
            borderRadius: '8px',
            width: '44px',
            height: '44px',
            transition: 'all 0.3s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1976d2';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 8px rgba(30, 136, 200, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = COLORS.secondary;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Modifier l'abonnement"
        >
          <i className="bi bi-pencil fs-5"></i>
        </Link>

        {/* Icône Supprimer */}
        <button
          className="btn d-flex align-items-center justify-content-center"
          onClick={() => setShowDeleteModal(true)}
          style={{
            color: COLORS.white,
            backgroundColor: COLORS.danger,
            borderColor: COLORS.danger,
            borderRadius: '8px',
            width: '44px',
            height: '44px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#d32f2f';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 8px rgba(244, 67, 54, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = COLORS.danger;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Supprimer l'abonnement"
        >
          <i className="bi bi-trash fs-5"></i>
        </button>
      </div>
    )}
  </div>

  {/* Bouton Retour en texte avec flèche (pas de tooltip) */}
  <div className="mt-4 pt-3 border-top" style={{ borderColor: COLORS.lightBg }}>
    <div className="text-center">
      <Link
        to="/abonnements"
        className="btn fw-semibold d-flex align-items-center justify-content-center mx-auto"
        style={{
          color: COLORS.primary,
          border: `1px solid ${COLORS.primary}`,
          backgroundColor: 'transparent',
          borderRadius: '8px',
          padding: '10px 24px',
          transition: 'all 0.3s ease',
          textDecoration: 'none',
          width: '100%'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E3F2FD';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'transparent';
        }}
      >
        <i className="bi bi-arrow-left me-2"></i>
        Retour à la liste
      </Link>
    </div>
  </div>
</div>
            </div>

            {/* Carte de résumé */}
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderTop: `4px solid ${COLORS.accent}`
            }}>
              <div className="card-header" style={{ 
                backgroundColor: COLORS.lightBg,
                borderBottom: `1px solid ${COLORS.lightBg}`,
                color: COLORS.primary,
                fontWeight: '600',
                fontFamily: "'Inter', sans-serif"
              }}>
                <i className="bi bi-card-checklist me-2"></i>
                Résumé
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '8px' }}>
                    <i className="bi bi-check-circle me-1" style={{ color: COLORS.success }}></i>
                    Ce qui est inclus
                  </div>
                  <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Accès complet à la plateforme
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Support technique dédié
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Mises à jour régulières
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Sauvegarde automatique des données
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      Sécurité renforcée
                    </li>
                  </ul>
                </div>
                
                <div className="alert alert-info mt-4" style={{ 
                  backgroundColor: '#E3F2FD',
                  borderColor: COLORS.accent,
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-shield-check me-2" style={{ color: COLORS.primary }}></i>
                    <div>
                      <strong>Sécurisé et fiable</strong>
                      <div>Votre abonnement est protégé par des mesures de sécurité avancées.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de suppression seulement */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ backgroundColor: COLORS.danger, color: COLORS.white, borderBottom: 'none', borderRadius: '12px 12px 0 0' }}>
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Supprimer l'abonnement
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="text-center mb-4">
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ 
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#FFEBEE',
                    color: COLORS.danger
                  }}>
                    <i className="bi bi-exclamation-triangle fs-3"></i>
                  </div>
                  <h5 style={{ color: COLORS.danger, fontWeight: '600' }}>Attention !</h5>
                  <p className="text-muted">
                    Êtes-vous sûr de vouloir supprimer définitivement l'abonnement de <strong>{abonnement.nom_societe}</strong> ?
                  </p>
                </div>
                
                <div className="alert alert-danger" style={{ backgroundColor: '#FFEBEE' }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2" style={{ color: COLORS.danger }}></i>
                    <div>
                      <strong>Cette action est irréversible</strong>
                      <div className="small">Toutes les données de cet abonnement seront définitivement supprimées.</div>
                    </div>
                  </div>
                </div>
                
                <div className="card mt-3" style={{ borderColor: '#e0e0e0' }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Société</span>
                      <span className="fw-bold">{abonnement.nom_societe}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Offre</span>
                      <span>{abonnement.offre?.libele_offre}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Statut</span>
                      <span style={{ color: statut.color }}>{statut.label}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                  style={{
                    color: COLORS.primary,
                    border: `1px solid ${COLORS.primary}`,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.danger,
                    borderColor: COLORS.danger,
                    borderRadius: '8px',
                    padding: '8px 20px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Suppression...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-2"></i>
                      Supprimer définitivement
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong,
        th,
        .modal-title {
          font-family: 'Inter', sans-serif !important;
        }
        
        .card-body,
        .alert,
        small,
        td,
        .form-control,
        .form-select,
        .modal-body {
          font-family: 'Roboto', sans-serif !important;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .badge {
          font-weight: 500;
        }
        
        /* Tooltip styles */
        .tooltip {
          font-family: 'Roboto', sans-serif;
          font-size: 0.8rem;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
            border-top: none !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .btn-sm {
            padding: 4px 8px !important;
            font-size: 0.8rem !important;
          }
          
          .modal-dialog {
            margin: 16px;
          }
          
          .d-flex.justify-content-center.gap-3 {
            flex-wrap: wrap;
            justify-content: center !important;
          }
        }
        
        .modal-backdrop {
          z-index: 1040;
        }
        
        .modal.show {
          display: block;
        }
      `}</style>
      
      {/* Initialisation des tooltips Bootstrap */}
      <script dangerouslySetInnerHTML={{
        __html: `
          document.addEventListener('DOMContentLoaded', function() {
            var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            var tooltipList = tooltipTriggerList.map(function(tooltipTriggerEl) {
              return new bootstrap.Tooltip(tooltipTriggerEl);
            });
          });
        `
      }} />

      <PaiementNitaAmanaModal
        show={showPaiementModal}
        idPaiement={abonnement?.id_paiement_pending}
        montant={abonnement?.montant}
        onClose={() => setShowPaiementModal(false)}
        onSuccess={() => {
          setShowPaiementModal(false);
          fetchAbonnementDetails();
        }}
      />
    </Layout>
  );
};

export default AbonnementsDetails;